import { useAsyncAction } from "../../hooks/useAsyncAction";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import type { OrgMembership, OrgSummary } from "@io-spoke/contracts/orgs";
import { setActiveOrgId } from "../../lib/authStorage";
import {
  getCurrentOrg,
  listOrgs,
  setActiveOrg,
  type OrgWithMembership,
} from "../../lib/orgs";
import { useUserPreferences } from "./UserPreferencesProvider";

type OrgContextValue = {
  orgs: OrgWithMembership[];
  currentOrg: OrgSummary | null;
  currentMembership: OrgMembership | null;
  isLoading: boolean;
  hasLoaded: boolean;
  isSwitching: boolean;
  error: string | null;
  refreshOrgs: () => Promise<void>;
  switchOrg: (orgId: string, options?: { silent?: boolean }) => Promise<void>;
  activateCreatedWorkspace: (workspace: OrgWithMembership) => void;
};

const OrgContext = createContext<OrgContextValue | null>(null);

type OrgProviderProps = {
  isAuthenticated: boolean;
  children: ReactNode;
};

const dedupeOrgs = (orgList: OrgWithMembership[]) => {
  const seen = new Map<string, OrgWithMembership>();
  for (const org of orgList) {
    const existing = seen.get(org.org.id);
    if (!existing) {
      seen.set(org.org.id, org);
      continue;
    }
    const existingActive = existing.membership.status === "active";
    const incomingActive = org.membership.status === "active";
    if (!existingActive && incomingActive) {
      seen.set(org.org.id, org);
    }
  }
  return Array.from(seen.values());
};

export function OrgProvider({ isAuthenticated, children }: OrgProviderProps) {
  const { refreshPreferences } = useUserPreferences();
  const [orgs, setOrgs] = useState<OrgWithMembership[]>([]);
  const [currentOrg, setCurrentOrg] = useState<OrgSummary | null>(null);
  const [currentMembership, setCurrentMembership] =
    useState<OrgMembership | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshToken = useRef(0);

  const invalidateRefreshes = useCallback(() => {
    refreshToken.current += 1;
    setIsLoading(false);
  }, []);

  const refreshOrgs = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    const token = refreshToken.current + 1;
    refreshToken.current = token;
    setIsLoading(true);
    setError(null);
    try {
      const [orgList, current] = await Promise.all([
        listOrgs(),
        getCurrentOrg(),
      ]);
      if (refreshToken.current !== token) {
        return;
      }
      setOrgs(dedupeOrgs(orgList));
      setCurrentOrg(current.org);
      setCurrentMembership(current.membership);
      setActiveOrgId(current.org?.id ?? null);
    } catch (err) {
      if (refreshToken.current !== token) return;
      const message = err instanceof Error ? err.message : "Unable to load workspaces.";
      setError(message);
      toast.error(message);
    } finally {
      if (refreshToken.current === token) {
        setHasLoaded(true);
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  const { run: runSwitch } = useAsyncAction();
  const switchOrg = useCallback(
    async (orgId: string, options?: { silent?: boolean }) => {
      if (!isAuthenticated) {
        return;
      }
      return runSwitch(async () => {
        setIsSwitching(true);
        setError(null);
        try {
          invalidateRefreshes();
          const result = await setActiveOrg({ orgId });
          setCurrentOrg(result.org);
          setCurrentMembership(result.membership);
          setActiveOrgId(result.org.id);
          setOrgs((prev) =>
            dedupeOrgs([
              ...prev,
              { org: result.org, membership: result.membership },
            ])
          );
          await refreshPreferences();
          if (!options?.silent) {
            toast.success(`Switched to ${result.org.name}.`);
          }
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Unable to switch workspace.";
          setError(message);
          toast.error(message);
          throw err;
        } finally {
          setIsSwitching(false);
        }
      });
    },
    [invalidateRefreshes, isAuthenticated, refreshPreferences, runSwitch]
  );

  const activateCreatedWorkspace = useCallback((workspace: OrgWithMembership) => {
    invalidateRefreshes();
    setCurrentOrg(workspace.org);
    setCurrentMembership(workspace.membership);
    setActiveOrgId(workspace.org.id);
    setOrgs((current) => dedupeOrgs([...current, workspace]));
    setError(null);
  }, [invalidateRefreshes]);

  useEffect(() => {
    if (!isAuthenticated) {
      setOrgs([]);
      setCurrentOrg(null);
      setCurrentMembership(null);
      setError(null);
      setActiveOrgId(null);
      setHasLoaded(false);
      return;
    }
    setHasLoaded(false);
    refreshOrgs();
  }, [isAuthenticated, refreshOrgs]);

  const value = useMemo<OrgContextValue>(
    () => ({
      orgs,
      currentOrg,
      currentMembership,
      isLoading,
      hasLoaded,
      isSwitching,
      error,
      refreshOrgs,
      switchOrg,
      activateCreatedWorkspace,
    }),
    [
      orgs,
      currentOrg,
      currentMembership,
      isLoading,
      hasLoaded,
      isSwitching,
      error,
      refreshOrgs,
      switchOrg,
      activateCreatedWorkspace,
    ]
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrgContext() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error("useOrgContext must be used within OrgProvider.");
  }
  return context;
}
